// frontend/src/actions/orderActions.js

import * as actionTypes from "../constants/orderConstants";
import api from "../utils/axiosInstance";
import {
  USER_FACING_FALLBACK,
  getApiErrorMessage,
} from "../utils/errorMessages";

const ORDERS_BASE_URL = "/orders";

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

function safeText(value = "") {
  return String(value ?? "").trim();
}

function normalizeUpper(value = "") {
  return safeText(value).toUpperCase();
}

function encodePath(value = "") {
  return encodeURIComponent(safeText(value));
}

function getHeader(error = {}, name = "") {
  const headers = error?.response?.headers || {};
  const target = safeText(name).toLowerCase();

  const key = Object.keys(headers || {}).find(
    (headerName) => safeText(headerName).toLowerCase() === target,
  );

  return key ? headers[key] : "";
}

function containsUnsafeCustomerStockCopy(value = "") {
  const text = safeText(value).toLowerCase();

  if (!text) return false;

  return (
    text.includes("reserved") ||
    text.includes("temporarily reserved") ||
    text.includes("allocated") ||
    text.includes("held") ||
    text.includes("held for")
  );
}

function containsSensitiveCustomerCopy(value = "") {
  const text = safeText(value).toLowerCase();

  return (
    text.includes("secret") ||
    text.includes("token") ||
    text.includes("authorization") ||
    text.includes("cookie") ||
    text.includes("jwt") ||
    text.includes("mongo") ||
    text.includes("api key") ||
    text.includes("client_secret") ||
    text.includes("access_token") ||
    text.includes("refresh_token")
  );
}

function looksLikeRawProviderCode(value = "") {
  const text = safeText(value);

  return Boolean(text && /^[A-Z0-9_.:-]{8,}$/.test(text));
}

function customerSafeMessage(value = "", fallback = USER_FACING_FALLBACK) {
  const text = safeText(value);

  if (!text) return fallback;
  if (containsSensitiveCustomerCopy(text)) return fallback;
  if (looksLikeRawProviderCode(text)) return fallback;

  return containsUnsafeCustomerStockCopy(text) ? fallback : text;
}

function normalizeIssue(issue = {}) {
  const fallback =
    issue?.code === "CART_EMPTY"
      ? "Your basket is empty."
      : "Please review your basket before continuing.";

  const message = customerSafeMessage(
    issue?.customerMessage ||
      issue?.message ||
      issue?.statusReason ||
      issue?.stockStatusReason ||
      issue?.unavailableReason ||
      issue?.disabledReason,
    fallback,
  );

  return {
    ...issue,
    message,
    statusReason: message,
    stockStatusReason: message,
    unavailableReason: message,
    disabledReason: message,
  };
}

function normalizeApiError(error, fallback = USER_FACING_FALLBACK) {
  const data = error?.response?.data || {};
  const fallbackMessage = getApiErrorMessage(error, { fallback });

  const issues = Array.isArray(data?.issues)
    ? data.issues.map(normalizeIssue)
    : [];

  const failures = Array.isArray(data?.failures) ? data.failures : [];

  const inventoryFailures = Array.isArray(data?.inventoryFailures)
    ? data.inventoryFailures
    : [];

  return {
    message: customerSafeMessage(
      data?.customerMessage ||
        data?.message ||
        error?.normalizedMessage,
      fallbackMessage,
    ),
    code: safeText(data?.code || error?.errorCode || error?.code),
    status: Number(error?.response?.status || error?.statusCode || 0),
    requestId: safeText(
      data?.requestId || error?.requestId || getHeader(error, "x-request-id"),
    ),
    retryAfterSeconds: Number(
      data?.retryAfterSeconds ||
        error?.retryAfterSeconds ||
        getHeader(error, "retry-after") ||
        0,
    ),
    issues,
    failures,
    inventoryFailures,
    payload: {
      code: safeText(data?.code || error?.errorCode || error?.code),
      status: Number(error?.response?.status || error?.statusCode || 0),
      requestId: safeText(
        data?.requestId ||
          error?.requestId ||
          getHeader(error, "x-request-id"),
      ),
    },
  };
}

function buildIdempotencyKey(prefix = "req") {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeOrderNumber(orderNumber = "") {
  return normalizeUpper(orderNumber);
}

function assertOrderNumber(orderNumber = "") {
  const safeOrderNumber = normalizeOrderNumber(orderNumber);

  if (!safeOrderNumber) {
    throw Object.assign(new Error("Order number is required."), {
      message: "Order number is required.",
      code: "ORDER_NUMBER_REQUIRED",
      status: 400,
      requestId: "",
      retryAfterSeconds: 0,
      issues: [],
      failures: [],
      inventoryFailures: [],
      payload: null,
    });
  }

  return safeOrderNumber;
}

function assertOrderId(orderId = "") {
  const safeOrderId = safeText(orderId);

  if (!safeOrderId) {
    throw Object.assign(new Error("Order id is required."), {
      message: "Order id is required.",
      code: "ORDER_ID_REQUIRED",
      status: 400,
      requestId: "",
      retryAfterSeconds: 0,
      issues: [],
      failures: [],
      inventoryFailures: [],
      payload: null,
    });
  }

  return safeOrderId;
}

/* -------------------------------------------------------------------------- */
/*                                  Create                                    */
/* -------------------------------------------------------------------------- */

export const createOrder =
  (payload = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.ORDER_CREATE_REQUEST });

    const idempotencyKey =
      safeText(payload?.idempotencyKey) || buildIdempotencyKey("order");

    try {
      const { data } = await api.post(ORDERS_BASE_URL, payload, {
        headers: {
          "Idempotency-Key": idempotencyKey,
        },
      });

      const responsePayload = {
        ...data,
        idempotencyKey,
      };

      dispatch({
        type: actionTypes.ORDER_CREATE_SUCCESS,
        payload: responsePayload,
      });

      return responsePayload;
    } catch (error) {
      const payloadError = normalizeApiError(
        error,
        "Could not create your order. Please review your basket and try again.",
      );

      dispatch({
        type: actionTypes.ORDER_CREATE_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

/* -------------------------------------------------------------------------- */
/*                                   Reads                                    */
/* -------------------------------------------------------------------------- */

export const listMyOrders =
  (params = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.ORDER_LIST_MY_REQUEST });

    try {
      const { data } = await api.get(ORDERS_BASE_URL, {
        params,
      });

      dispatch({
        type: actionTypes.ORDER_LIST_MY_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(
        error,
        "Could not load your orders.",
      );

      dispatch({
        type: actionTypes.ORDER_LIST_MY_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const getMyOrders = listMyOrders;

export const getOrderById = (orderId) => async (dispatch) => {
  dispatch({ type: actionTypes.ORDER_DETAILS_REQUEST });

  try {
    const safeOrderId = assertOrderId(orderId);

    const { data } = await api.get(
      `${ORDERS_BASE_URL}/id/${encodePath(safeOrderId)}`,
    );

    dispatch({
      type: actionTypes.ORDER_DETAILS_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payloadError = normalizeApiError(error, "Could not load this order.");

    dispatch({
      type: actionTypes.ORDER_DETAILS_FAIL,
      payload: payloadError,
    });

    throw payloadError;
  }
};

export const getOrderByNumber = (orderNumber) => async (dispatch) => {
  dispatch({ type: actionTypes.ORDER_DETAILS_REQUEST });

  try {
    const safeOrderNumber = assertOrderNumber(orderNumber);

    const { data } = await api.get(
      `${ORDERS_BASE_URL}/number/${encodePath(safeOrderNumber)}`,
    );

    dispatch({
      type: actionTypes.ORDER_DETAILS_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payloadError = normalizeApiError(error, "Could not load this order.");

    dispatch({
      type: actionTypes.ORDER_DETAILS_FAIL,
      payload: payloadError,
    });

    throw payloadError;
  }
};

export const getOrderStatus = (orderNumber) => async (dispatch) => {
  dispatch({ type: actionTypes.ORDER_STATUS_REQUEST });

  try {
    const safeOrderNumber = assertOrderNumber(orderNumber);

    const { data } = await api.get(
      `${ORDERS_BASE_URL}/status/${encodePath(safeOrderNumber)}`,
    );

    dispatch({
      type: actionTypes.ORDER_STATUS_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payloadError = normalizeApiError(
      error,
      "Could not refresh order status.",
    );

    dispatch({
      type: actionTypes.ORDER_STATUS_FAIL,
      payload: payloadError,
    });

    throw payloadError;
  }
};

/* -------------------------------------------------------------------------- */
/*                                Cancellation                                */
/* -------------------------------------------------------------------------- */

export const cancelOrder =
  ({ orderNumber, reason = "", idempotencyKey = "" } = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.ORDER_CANCEL_REQUEST });

    try {
      const safeOrderNumber = assertOrderNumber(orderNumber);

      const safeIdempotencyKey =
        safeText(idempotencyKey) || buildIdempotencyKey("order-cancel");

      const { data } = await api.post(
        `${ORDERS_BASE_URL}/${encodePath(safeOrderNumber)}/cancel`,
        {
          reason: safeText(reason),
        },
        {
          headers: {
            "Idempotency-Key": safeIdempotencyKey,
          },
        },
      );

      const responsePayload = {
        ...data,
        idempotencyKey: safeIdempotencyKey,
      };

      dispatch({
        type: actionTypes.ORDER_CANCEL_SUCCESS,
        payload: responsePayload,
      });

      return responsePayload;
    } catch (error) {
      const payloadError = normalizeApiError(
        error,
        "Could not cancel this order.",
      );

      dispatch({
        type: actionTypes.ORDER_CANCEL_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

/* -------------------------------------------------------------------------- */
/*                                  Resets                                    */
/* -------------------------------------------------------------------------- */

export const resetOrderCreate = () => ({
  type: actionTypes.ORDER_CREATE_RESET,
});

export const resetOrderListMy = () => ({
  type: actionTypes.ORDER_LIST_MY_RESET,
});

export const resetOrderDetails = () => ({
  type: actionTypes.ORDER_DETAILS_RESET,
});

export const resetOrderStatus = () => ({
  type: actionTypes.ORDER_STATUS_RESET,
});

export const resetOrderCancel = () => ({
  type: actionTypes.ORDER_CANCEL_RESET,
});

export const resetOrderError = () => ({
  type: actionTypes.ORDER_RESET_ERROR,
});

export const resetOrderMeta = () => ({
  type: actionTypes.ORDER_RESET_META,
});

export const resetOrderState = () => ({
  type: actionTypes.ORDER_RESET_STATE,
});
